import axios from "axios";

import type { Post } from "../types";
import {
  type LoadedDraft,
  normalizeDraft,
} from "selectors/loaded-posts-normalizer";
import {
  standardizationPostData,
  standardizationUpcomingPosts,
} from "utils/posts";

type EditablePostClusterResponse = {
  drafts: LoadedDraft[];
  queues: Post[];
  upcomings: Post[];
  archives: Post[];
};

export type EditablePostCluster = {
  posts: Post[];
  archives: Post[];
};

const postIdentifier = (post: Pick<Post, "_id" | "id">): string | null =>
  post._id || post.id || null;

export const uniqueClusterPosts = <T extends Pick<Post, "_id" | "id">>(
  posts: T[]
): T[] => {
  const identifiers = new Set<string>();
  return posts.filter((post) => {
    const identifier = postIdentifier(post);
    if (!identifier) return true;
    if (identifiers.has(identifier)) return false;
    identifiers.add(identifier);
    return true;
  });
};

/**
 * Loads the same editable cluster membership shown by Edit Posts. Archives are
 * returned separately because the modal only adds the requested archived post.
 */
export async function fetchEditablePostCluster(
  id: string
): Promise<EditablePostCluster> {
  const response = await axios.get<EditablePostClusterResponse>(
    `/schedules/cluster/${id}`
  );
  const normalizedDrafts = await Promise.all(
    response.data.drafts.map((draft) => normalizeDraft(draft))
  );
  const posts = uniqueClusterPosts([
    ...normalizedDrafts.map((draft) => draft.schedule),
    ...(await standardizationUpcomingPosts(response.data.queues, [])),
    ...(await standardizationUpcomingPosts(response.data.upcomings, [])),
  ]);

  return {
    posts,
    archives: uniqueClusterPosts(
      await Promise.all(
        (response.data.archives || []).map((post) =>
          standardizationPostData(post)
        )
      )
    ),
  };
}
